import { useExpose } from "#ce/composables/useExpose";

/**
 * Installs the property and method surface a native `<textarea>` carries on the
 * `sp-textarea` element itself, so consumers read and drive the field through
 * the element rather than reaching into its shadow root.
 *
 * The descriptors are configurable: an element that is moved in the DOM is
 * unmounted and mounted again, and the accessors of the new mount have to
 * replace those of the old one.
 *
 * @param {Object} api
 * @param {import('vue').Ref<HTMLTextAreaElement|null>} api.textarea
 * @param {Readonly<import('vue').Ref<String>>} api.text
 * @param {Readonly<import('vue').Ref<String>>} api.defaultValue
 * @param {() => NodeList|null} api.labels
 * @param {(value: *) => void} api.setText
 * @param {(value: *) => void} api.setDefaultValue
 * @param {(message: *) => void} api.setCustomValidity
 */
export function useTextareaHostApi({
  textarea,
  text,
  defaultValue,
  labels,
  setText,
  setDefaultValue,
  setCustomValidity,
}) {
  const { exposeProperties, exposeMethods } = useExpose();

  exposeProperties({
    value: {
      configurable: true,
      get: () => text.value,
      set: setText,
    },
    defaultValue: {
      configurable: true,
      get: () => defaultValue.value,
      set: setDefaultValue,
    },
    textLength: {
      configurable: true,
      get: () => text.value.length,
    },
    type: {
      configurable: true,
      get: () => "textarea",
    },
    labels: {
      configurable: true,
      get: labels,
    },
    textareaElement: {
      configurable: true,
      get: () => textarea.value,
    },
    selectionStart: selectionProperty("selectionStart"),
    selectionEnd: selectionProperty("selectionEnd"),
    selectionDirection: selectionProperty("selectionDirection"),
  });

  exposeMethods({
    setCustomValidity,

    select() {
      textarea.value?.select();
    },

    setSelectionRange(start, end, direction) {
      textarea.value?.setSelectionRange(start, end, direction);
    },

    setRangeText(...args) {
      const element = textarea.value;

      if (!element) {
        return;
      }

      element.setRangeText(...args);
      setText(element.value);
    },
  });

  /**
   * @param {"selectionStart"|"selectionEnd"|"selectionDirection"} key
   * @returns {PropertyDescriptor}
   */
  function selectionProperty(key) {
    return {
      configurable: true,
      get: () => textarea.value?.[key] ?? null,
      set: (value) => {
        if (textarea.value) {
          textarea.value[key] = value;
        }
      },
    };
  }
}
